import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Driver APIs
export const driverAPI = {
  register: (data) => api.post('/drivers/register', data),
  getAll: () => api.get('/drivers'),
  getAvailable: () => api.get('/drivers/available'),
  toggleAvailability: () => api.patch('/drivers/availability'),
  getEarnings: () => api.get('/drivers/earnings'),
  getMyStatus: () => api.get('/drivers/my-status'),
};

// Job APIs
export const jobAPI = {
  create: (data) => api.post('/jobs', data),
  validateRoute: (data) => api.post('/jobs/validate-route', data),
  getAll: (params = {}) => api.get('/jobs', { params }),
  getAvailable: () => api.get('/jobs/available'),
  getMyJobs: () => api.get('/jobs/my-jobs'),
  getById: (id) => api.get(`/jobs/${id}`),
  assign: (id, driverId) => api.patch(`/jobs/${id}/assign`, { driver_id: driverId }),
  accept: (id) => api.patch(`/jobs/${id}/accept`),
  start: (id) => api.patch(`/jobs/${id}/start`),
  complete: (id) => api.patch(`/jobs/${id}/complete`),
  cancel: (id) => api.patch(`/jobs/${id}/cancel`),
  poll: (since) => api.get('/jobs/poll', { params: { since } }),
  getDriverLocation: (id) => api.get(`/jobs/${id}/driver-location`),
};

// Location APIs (for drivers)
export const locationAPI = {
  update: (data) => api.post('/drivers/location', data),
};

// Payment APIs
export const paymentAPI = {
  createCheckout: (jobId, originUrl) => api.post('/payments/checkout', { job_id: jobId, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Tip APIs
export const tipAPI = {
  createCheckout: (jobId, amount, originUrl) => api.post('/tips/checkout', { job_id: jobId, amount, origin_url: originUrl }),
  getJobTip: (jobId) => api.get(`/tips/job/${jobId}`),
  getStatus: (sessionId) => api.get(`/tips/status/${sessionId}`),
};

// Pricing API
export const pricingAPI = {
  getAll: () => api.get('/pricing'),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getAllJobs: (params = {}) => api.get('/admin/jobs', { params }),
  getAllUsers: () => api.get('/admin/users'),
  getAnalytics: () => api.get('/admin/analytics'),
  getControlTower: () => api.get('/admin/control-tower'),
  getDriverLocations: () => api.get('/drivers/locations/all'),
  broadcastMessage: (content, audience) => api.post('/admin/broadcast', { content, audience }),
  directMessage: (userId, content) => api.post(`/admin/message/${userId}`, { content }),
  sendDigest: () => api.post('/admin/send-digest'),
  deleteJob: (jobId) => api.delete(`/admin/jobs/${jobId}`),
  reopenJob: (jobId) => api.post(`/admin/jobs/${jobId}/reopen`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getSchedulerStatus: () => api.get('/scheduler/status'),
  triggerDigest: () => api.post('/scheduler/trigger-digest'),
  // Driver approval
  getPendingDrivers: () => api.get('/admin/pending-drivers'),
  getDriverDetails: (driverId) => api.get(`/admin/driver/${driverId}`),
  approveDriver: (driverId) => api.post(`/admin/driver/${driverId}/approve`),
  rejectDriver: (driverId, reason) => api.post(`/admin/driver/${driverId}/reject`, { reason }),
  // Hub management
  listHubs: () => api.get('/admin/hubs'),
  createHub: (data) => api.post('/admin/hubs', data),
  updateHub: (id, data) => api.patch(`/admin/hubs/${id}`, data),
  deleteHub: (id) => api.delete(`/admin/hubs/${id}`),
};

// Public Hubs API
export const hubAPI = {
  list: () => api.get('/hubs'),
  checkCoverage: (address, email) => api.get('/hubs/coverage', { params: { address, ...(email ? { email } : {}) } }),
  coverageLeads: () => api.get('/admin/coverage-leads'),
};

// Notification preferences (quiet hours + per-type toggles)
export const notifPrefsAPI = {
  get: () => api.get('/notification-preferences'),
  update: (data) => api.patch('/notification-preferences', data),
};

// Disputes API
export const disputeAPI = {
  // User/Driver
  create: (data) => api.post('/disputes', data),
  myDisputes: () => api.get('/disputes/my'),
  get: (id) => api.get(`/disputes/${id}`),
  // Admin
  adminList: (status) => api.get('/admin/disputes', { params: status ? { status } : {} }),
  adminUpdate: (id, data) => api.patch(`/admin/disputes/${id}`, data),
  adminRefund: (id, data) => api.post(`/admin/disputes/${id}/refund`, data),
};

// Route Matching API
export const routeMatchAPI = {
  findMatches: (data) => api.post('/routes/match', data),
};

// Map API
export const mapAPI = {
  getData: () => api.get('/map/data'),
};

// Driver Payout API
export const payoutAPI = {
  getReport: (period = 'week') => api.get('/drivers/payout-report', { params: { period } }),
  exportReport: (format, period = 'week') => api.get('/drivers/payout-report/export', {
    params: { format, period },
    responseType: 'blob'
  }),
  adminBulkExport: (format, period = 'week') => api.get('/admin/payouts/bulk-export', {
    params: { format, period },
    responseType: 'blob'
  }),
};

// Rating APIs
export const ratingAPI = {
  create: (data) => api.post('/ratings', data),
  getMyRatings: () => api.get('/ratings/me'),
  getUserRatings: (userId) => api.get(`/ratings/user/${userId}`),
  getJobRatings: (jobId) => api.get(`/ratings/job/${jobId}`),
};

// Driver Route APIs
export const driverRouteAPI = {
  create: (data) => api.post('/driver-routes', data),
  getAll: () => api.get('/driver-routes'),
  getMyRoutes: () => api.get('/driver-routes/my-routes'),
  getById: (id) => api.get(`/driver-routes/${id}`),
  update: (id, data) => api.patch(`/driver-routes/${id}`, data),
  delete: (id) => api.delete(`/driver-routes/${id}`),
};

// Route Booking APIs
export const routeBookingAPI = {
  create: (data) => api.post('/route-bookings', data),
  getAll: () => api.get('/route-bookings'),
  cancel: (id) => api.patch(`/route-bookings/${id}/cancel`),
};

// Notification APIs
export const notificationAPI = {
  getAll: (unreadOnly = false, limit = 50) => api.get(`/notifications?unread_only=${unreadOnly}&limit=${limit}`),
  getUnreadCount: () => api.get('/notifications/count'),
  markRead: (ids) => api.post('/notifications/mark-read', { notification_ids: ids }),
  markAllRead: () => api.post('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Chat APIs
export const chatAPI = {
  sendMessage: (recipientId, content) => api.post('/messages', { recipient_id: recipientId, content }),
  getConversations: () => api.get('/conversations'),
  getMessages: (conversationId, limit = 50) => api.get(`/conversations/${conversationId}/messages?limit=${limit}`),
  getUnreadCount: () => api.get('/messages/unread-count'),
  // Enhanced chat
  sendChatMessage: (recipientId, content) => api.post('/chat/send', { recipient_id: recipientId, content }),
  getEnhancedConversations: () => api.get('/chat/conversations'),
  getChatMessages: (conversationId, limit = 50) => api.get(`/chat/conversations/${conversationId}/messages?limit=${limit}`),
  createGroup: (name, memberIds) => api.post('/chat/group', { name, member_ids: memberIds }),
  sendGroupMessage: (conversationId, content) => api.post(`/chat/group/${conversationId}/message`, { content }),
  sendTyping: (conversationId, isTyping) => api.post('/chat/typing', { conversation_id: conversationId, is_typing: isTyping }),
  getTyping: (conversationId) => api.get(`/chat/typing/${conversationId}`),
  markRead: (conversationId) => api.post(`/chat/read/${conversationId}`),
};

// Friend APIs
export const friendAPI = {
  sendRequest: (userId) => api.post(`/friends/request/${userId}`),
  acceptRequest: (requestId) => api.post(`/friends/request/${requestId}/accept`),
  declineRequest: (requestId) => api.post(`/friends/request/${requestId}/decline`),
  cancelRequest: (requestId) => api.post(`/friends/request/${requestId}/cancel`),
  getReceivedRequests: () => api.get('/friends/requests/received'),
  getSentRequests: () => api.get('/friends/requests/sent'),
  getFriends: () => api.get('/friends'),
  removeFriend: (friendId) => api.delete(`/friends/${friendId}`),
  checkFriendship: (userId) => api.get(`/friends/check/${userId}`),
};

// People Search API
export const peopleAPI = {
  search: (q = '') => api.get(`/people/search?q=${encodeURIComponent(q)}`),
};

// Online Status API
export const statusAPI = {
  heartbeat: () => api.post('/status/heartbeat'),
  setOffline: () => api.post('/status/offline'),
  updateText: (statusText) => api.post('/status/text', { status_text: statusText }),
  getStatus: (userId) => api.get(`/status/${userId}`),
};

// Story APIs
export const storyAPI = {
  create: (data) => api.post('/stories', data),
  getAll: () => api.get('/stories'),
  view: (storyId) => api.post(`/stories/${storyId}/view`),
  getViewers: (storyId) => api.get(`/stories/${storyId}/viewers`),
  delete: (storyId) => api.delete(`/stories/${storyId}`),
};

// Task Marketplace APIs
export const taskAPI = {
  create: (data) => api.post('/tasks', data),
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/tasks?${query}`);
  },
  getMy: () => api.get('/tasks/my'),
  getById: (id) => api.get(`/tasks/${id}`),
  cancel: (id) => api.patch(`/tasks/${id}/cancel`),
  complete: (id) => api.patch(`/tasks/${id}/complete`),
  getBids: (taskId) => api.get(`/tasks/${taskId}/bids`),
};

// Bidding APIs
export const bidAPI = {
  create: (data) => api.post('/bids', data),
  getMy: () => api.get('/bids/my'),
  accept: (bidId) => api.patch(`/bids/${bidId}/accept`),
};

// Follow APIs
export const followAPI = {
  follow: (userId) => api.post(`/follow/${userId}`),
  unfollow: (userId) => api.delete(`/follow/${userId}`),
  getFollowing: () => api.get('/following'),
  getFollowers: () => api.get('/followers'),
  checkFollowing: (userId) => api.get(`/follow/check/${userId}`),
};

// Feed APIs
export const feedAPI = {
  getCommunityFeed: (limit = 50) => api.get(`/feed?limit=${limit}`),
  getFollowingFeed: (limit = 50) => api.get(`/feed/following?limit=${limit}`),
  createPost: (content, imageUrl, opts = {}) => api.post('/feed/post', { content, image_url: imageUrl || null, ...opts }),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  likePost: (postId) => api.post(`/feed/${postId}/like`),
  pinPost: (postId) => api.post(`/feed/${postId}/pin`),
  commentOnPost: (postId, content) => api.post(`/feed/${postId}/comment`, { content }),
  getComments: (postId) => api.get(`/feed/${postId}/comments`),
  deletePost: (postId) => api.delete(`/feed/${postId}`),
};

// User Profile APIs
export const profileAPI = {
  getProfile: (userId) => api.get(`/users/${userId}/profile`),
};

export default api;
